import React from "react";
import { Redirect } from "react-router-dom";
import qs from "qs";
import { compose, withApollo } from "react-apollo";
import {
  Elements,
  injectStripe,
  CardNumberElement,
  CardCVCElement,
  CardExpiryElement,
} from "react-stripe-elements";
import { FormField, FormFieldset } from "mineral-ui/Form";
import Select from "mineral-ui/Select";
import TextInput from "mineral-ui/TextInput";
import Button from "mineral-ui/Button";
import { Box } from "mineral-ui";
import { H2, P } from "../components/html";
import { PlanName } from "../components/billing";
import COUNTRIES from "../countries";
import { withSetStripePlan } from "../SetStripePlanMutation";

import Link from "../components/Link";
import { withRefreshStripePlan } from "../RefreshStripeMutation";

function isValidVATNumber(vatNumber) {
  return (
    !!vatNumber && !!vatNumber.replace(/ +/g, "").match(/^[A-Z0-9]{2,13}$/)
  );
}

class _CheckoutForm extends React.Component {
  state = {
    working: false,
    success: false,
    vatNumber: "",
    name: "",
    address_line1: "",
    address_line2: "",
    address_city: "",
    address_state: "",
    address_zip: "",
  };

  getStatus() {
    const { plan } = this.props;
    const selectedCountry = COUNTRIES.find(c => c.code === this.state.country);
    const { currency, amount } = plan.planInfo;
    const currencySymbol =
      {
        GBP: "£",
        USD: "$",
        EUR: "€",
      }[currency.toUpperCase()] || "";

    const countryHasTax = selectedCountry && selectedCountry.taxPercent > 0;
    const shouldAskForVatNumber =
      countryHasTax && selectedCountry.noTaxIfVATRegistered;
    const omitVat =
      shouldAskForVatNumber && isValidVATNumber(this.state.vatNumber);
    const taxPercent = selectedCountry
      ? omitVat
        ? 0
        : selectedCountry.taxPercent
      : 0;
    const amountIncludingTax = Math.round(amount * ((100 + taxPercent) / 100));
    const includingVAT = countryHasTax
      ? omitVat
        ? "excluding VAT"
        : `including VAT (@ ${taxPercent}%)`
      : "";

    const isVatNumberOkay =
      !shouldAskForVatNumber ||
      !this.state.vatNumber ||
      isValidVATNumber(this.state.vatNumber);
    const isCountryOkay = !!this.state.country;
    const ready = isCountryOkay && isVatNumberOkay;
    return {
      plan,
      ready,
      selectedCountry,
      shouldAskForVatNumber,
      amountIncludingTax,
      includingVAT,
      currency: currency.toUpperCase(),
      currencySymbol,
    };
  }

  handleSubmit = async e => {
    e.preventDefault();
    const { stripe, refreshStripePlan } = this.props;
    const response = await stripe.createToken({
      name: this.state.name,
      address_line1: this.state.address_line1,
      address_line2: this.state.address_line2,
      address_city: this.state.address_city,
      address_state: this.state.address_state,
      address_zip: this.state.address_zip,
      address_country: this.state.country,
    });
    const { token } = response;
    if (!token) {
      this.setState({
        errorMessage: `The credit card details you entered don't appear to be valid; please check them and try again`,
      });
      return;
    }
    const country = token && token.card && token.card.country;
    if (!this.state.country) {
      this.setState({ country });
      return;
    }
    const { ready, shouldAskForVatNumber } = this.getStatus();
    if (!ready) {
      return;
    }
    if (country !== this.state.country) {
      this.setState({
        errorMessage: `The country you selected, ${this.state.country}, does not match that of your payment card, ${country}. If you believe this is a bug, please get in touch; otherwise we must insist that you use a payment card registered in your home country for tax reasons.`,
      });
      return;
    }
    try {
      this.setState({ working: true });
      const { data } = await this.props.setStripePlan({
        variables: {
          planId: this.props.plan.id,
          tokenId: token.id,
          vatNumber: shouldAskForVatNumber ? this.state.vatNumber : null,
          cancel: false,
        },
      });
      const invoice =
        data && data.setStripePlan && data.setStripePlan.stripeInvoice;
      const invoiceIsOkay = inv =>
        inv && inv.paymentIntentStatus === "succeeded";
      if (invoiceIsOkay(invoice)) {
        this.props.client.resetStore();
        this.setState({ success: true, working: false });
      } else if (
        invoice &&
        (invoice.paymentIntentStatus === "requires_action" ||
          invoice.paymentIntentStatus === "requires_confirmation")
      ) {
        stripe
          .handleCardPayment(invoice.paymentIntentClientSecret)
          .then(refreshStripePlan)
          .then(({ data: data2 }) => {
            // See if it's good
            const invoice2 =
              data2 &&
              data2.refreshStripePlan &&
              data2.refreshStripePlan.stripeInvoice;
            if (!invoiceIsOkay(invoice2)) {
              throw new Error("Still unconfirmed");
            }
          })
          .then(
            () => {
              // Success
              this.props.client.resetStore();
              this.setState({ success: true, working: false });
            },
            err => {
              console.error(err);
              this.setState({
                success: false,
                working: false,
                errorMessage: `The payment failed, please check your card details and whether you have sufficient funds, and try again.`,
              });
            }
          );
      } else {
        this.setState({
          success: false,
          working: false,
          errorMessage: `The card was declined for creating a subscription (possibly due to SCA?), please choose a different card. Code: '${invoice.paymentIntentStatus}'`,
        });
      }
    } catch (err) {
      this.setState({
        working: false,
        errorMessage: err.message
          ? `An error occurred: ${err.message}`
          : "An unknown error occurred.",
      });
    }
  };

  render() {
    if (this.state.success) {
      return (
        <div>
          <h2>Operation completed successfully</h2>
          <P>
            Please return to <Link to="/settings">Settings</Link>
          </P>
        </div>
      );
    }
    const {
      ready,
      shouldAskForVatNumber,
      amountIncludingTax,
      includingVAT,
      currency,
      currencySymbol,
      selectedCountry,
    } = this.getStatus();

    const items = COUNTRIES.map(c => ({
      text: c.name,
      value: c.code,
    }));
    const selectedItem = selectedCountry
      ? items.find(i => i.value === selectedCountry.code)
      : null;

    return (
      <div>
        {this.state.working ? (
          <div>
            <h2>Please wait...</h2>
            <P>We're communicating with the payment processor...</P>
          </div>
        ) : null}
        <form
          onSubmit={this.handleSubmit}
          style={this.state.working ? { display: "none" } : null}
        >
          {this.state.errorMessage ? (
            <P color="red">An error occurred, see bottom of form.</P>
          ) : null}
          <FormFieldset legend="Secure card payment">
            <FormField label="Name on card">
              <TextInput
                value={this.state.name}
                onChange={e => this.setState({ name: e.target.value })}
              />
            </FormField>
            <br />
            <FormField label="Card number">
              <CardNumberElement
                onReady={el => el.focus()}
                onChange={() => {
                  if (this.state.errorMessage)
                    this.setState({ errorMessage: null });
                }}
              />
            </FormField>
            <br />
            <FormField label="Expiration date">
              <CardExpiryElement />
            </FormField>
            <br />
            <FormField label="CVC">
              <CardCVCElement />
            </FormField>
          </FormFieldset>
          <br />
          <FormFieldset legend="Billing address">
            <FormField label="Address">
              <TextInput
                value={this.state.address_line1}
                onChange={e => this.setState({ address_line1: e.target.value })}
              />
            </FormField>
            <br />
            <FormField label="Address line 2" hideLabel>
              <TextInput
                value={this.state.address_line2}
                onChange={e => this.setState({ address_line2: e.target.value })}
              />
            </FormField>
            <br />
            <FormField label="Town / City">
              <TextInput
                value={this.state.address_city}
                onChange={e => this.setState({ address_city: e.target.value })}
              />
            </FormField>
            <br />
            <FormField label="County or state">
              <TextInput
                value={this.state.address_state}
                onChange={e => this.setState({ address_state: e.target.value })}
              />
            </FormField>
            <br />
            <FormField label="Postal code">
              <TextInput
                value={this.state.address_zip}
                onChange={e => this.setState({ address_zip: e.target.value })}
              />
            </FormField>
            <br />
            <FormField label="Country">
              <Select
                data={items}
                selectedItem={selectedItem}
                value={this.state.country}
                onChange={item =>
                  this.setState({ country: item.value, errorMessage: null })
                }
              />
            </FormField>
          </FormFieldset>
          <br />
          {shouldAskForVatNumber ? (
            <FormFieldset legend="VAT details">
              <FormField
                label="VAT number"
                caption="If you are an EU company, provide your VAT (consumption tax)
              registration number."
              >
                <TextInput
                  prefix={this.state.country}
                  placeholder="000 0000 00"
                  value={this.state.vatNumber}
                  onChange={e => this.setState({ vatNumber: e.target.value })}
                />
              </FormField>
              <br />
            </FormFieldset>
          ) : null}
          {this.state.errorMessage ? (
            <P color="red">{this.state.errorMessage}</P>
          ) : null}
          <P>
            Total: {currency.toUpperCase()}
            {currencySymbol}
            {(amountIncludingTax / 100).toFixed(2)}
            /mo {includingVAT}
          </P>
          <P>
            <Button primary disabled={!ready} type="submit">
              Confirm order
            </Button>
          </P>
        </form>
      </div>
    );
  }
}

const CheckoutForm = compose(
  injectStripe,
  withApollo,
  withSetStripePlan,
  withRefreshStripePlan
)(_CheckoutForm);

class ChangePlan extends React.Component {
  state = {};

  render() {
    const {
      data,
      location: { search },
    } = this.props;
    const params = search ? qs.parse(search.substr(1)) : {};
    const planId = parseInt(params.plan, 10);
    const plan = data.allPlans.nodes.find(p => p.id === planId);
    if (!plan || plan.source !== "stripe") {
      return <Redirect to="/settings" />;
    }
    const { currency } = plan;
    return (
      <div>
        <Box marginVertical="xl">
          <H2 align="center">
            {plan.viewerHasPlan ? "Updating subscription to" : "Subscribing to"}{" "}
            {plan.name}
          </H2>
        </Box>
        {plan.viewerHasPlan ? (
          <P>
            Please re-enter all billing details to change your payment card /
            address / tax info.
          </P>
        ) : (
          <P>
            You've chosen the <PlanName prefix={plan.slug.split("-")[0]} />, to
            be paid monthly in {currency.toUpperCase()}. If you wish to select a
            different currency, please return to{" "}
            <Link to="/settings">settings</Link>.
          </P>
        )}
        <Elements>
          <div style={{ maxWidth: "30rem", margin: "auto" }}>
            <CheckoutForm plan={plan} />
          </div>
        </Elements>
      </div>
    );
  }
}

export default ChangePlan;
